import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowDownWideNarrow, ChevronLeft, Keyboard } from 'lucide-react';
import clsx from 'clsx';

import { useApi } from '../api-context';
import { useHotkeys } from '../hooks/useHotkeys';
import { usePreviewWindow } from '../hooks/useImageUrl';
import Filmstrip from '../components/Filmstrip';
import BucketBar from '../components/BucketBar';
import PreviewPane from '../components/PreviewPane';
import ShortcutSheet from '../components/ShortcutSheet';
import { ProgressBar, Spinner } from '../components/Primitives';
import type { Bucket, MediaFilter, MediaItem, Project } from '../../types';
import type { AssistMarkers } from '../../api';
// Pure functions, imported by anyone and importing nobody — the one layer the
// UI is allowed to reach for directly.
import { stacksOf, flatten, depthOf } from '../../domain/stacks';

/** Which frames the strip is narrowed to. The three are mutually exclusive. */
type Lens = 'all' | 'soft' | 'picks';

const FILTERS: { value: MediaFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'reviewed', label: 'Reviewed' },
];

interface Change {
  mediaId: number;
  from: number | null;
  to: number | null;
}

export default function Review() {
  const api = useApi();
  const navigate = useNavigate();
  const { projectId: raw } = useParams();
  const projectId = Number(raw);

  const [project, setProject] = useState<Project | null>(null);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [index, setIndex] = useState(0);
  const [filter, setFilter] = useState<MediaFilter>('all');
  const [showKeys, setShowKeys] = useState(false);
  const [markers, setMarkers] = useState<AssistMarkers>(new Map());
  const [params, setParams] = useSearchParams();
  // The shoot page can hand the lens over, so "show me the picks" stays one
  // decision rather than being made again on arrival.
  const [lens, setLens] = useState<Lens>(() => {
    const asked = params.get('lens');
    return asked === 'picks' || asked === 'soft' ? asked : 'all';
  });
  /**
   * Turn a lens on or off, and say so in the address bar.
   *
   * The URL is the record of what is on screen. Left stale, it disagreed with
   * the view the moment the chip was clicked — and worse, a reload would then
   * silently re-apply a lens the photographer had just turned off.
   */
  const changeLens = useCallback(
    (next: Lens) => {
      setLens(next);
      setIndex(0);
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          if (next === 'all') updated.delete('lens');
          else updated.set('lens', next);
          return updated;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const [sortSharpest, setSortSharpest] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());
  const [loading, setLoading] = useState(true);

  const past = useRef<Change[]>([]);
  const future = useRef<Change[]>([]);
  const [, bumpHistory] = useState(0);

  /* ---------- load ---------- */

  const reloadBuckets = useCallback(() => {
    setBuckets(api.listBuckets(projectId));
    setProject(api.getProject(projectId));
  }, [api, projectId]);

  useEffect(() => {
    const found = api.openProject(projectId);
    if (!found) {
      navigate('/', { replace: true });
      return;
    }
    setProject(found);
    setBuckets(api.listBuckets(projectId));

    const ids = api.mediaIds(projectId, filter);
    setItems(api.getMediaByIds(ids));

    // Resume where the photographer left off.
    if (found.lastViewedMediaId) {
      const at = ids.indexOf(found.lastViewedMediaId);
      if (at >= 0) setIndex(at);
    }
    setLoading(false);
    setMarkers(api.assistMarkers(projectId));

    // The sharpness pass runs quietly in the background: it costs no download
    // and needs no permission, so there is nothing to ask about and the amber
    // dots simply appear as frames are scored.
    //
    // Not cancelled when the screen is left, and not started twice: the run
    // belongs to the api layer, so if the photographer already said yes to a
    // full review on the shoot page, this attaches to that run rather than
    // racing a second one against it.
    const unsubscribe = api.onAssistProgress(projectId, () =>
      setMarkers(api.assistMarkers(projectId)),
    );
    void api.startAssist(projectId, false).catch(() => undefined);

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projectId]);

  // Changing filter rebuilds the list, keeping the current photo if it survives.
  useEffect(() => {
    if (loading) return;
    const currentId = items[index]?.id;
    const next = api.getMediaByIds(api.mediaIds(projectId, filter));
    setItems(next);

    const at = currentId ? next.findIndex((item) => item.id === currentId) : -1;
    setIndex(at >= 0 ? at : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // The lens narrows whatever the bucket filter already produced, rather than
  // replacing it, so the two compose the way a photographer expects.
  const lensed = useMemo(() => {
    if (lens === 'soft') return items.filter((item) => markers.get(item.id)?.isSoft);
    if (lens === 'picks') return items.filter((item) => markers.get(item.id)?.isPick);
    return items;
  }, [items, lens, markers]);

  const stacks = useMemo(() => stacksOf(lensed, markers), [lensed, markers]);

  const visible = useMemo(() => {
    // Sharpest-first throws away capture order, and a stack is a statement
    // about capture order. Showing both at once would be a stack that means
    // nothing, so sorting simply un-stacks.
    if (sortSharpest) {
      return [...lensed].sort(
        (a, b) => (markers.get(b.id)?.sharpness ?? 0) - (markers.get(a.id)?.sharpness ?? 0),
      );
    }
    return flatten(stacks, expanded);
  }, [lensed, stacks, expanded, sortSharpest, markers]);

  const depth = useMemo(
    () => (sortSharpest ? new Map<number, number>() : depthOf(stacks, expanded)),
    [stacks, expanded, sortSharpest],
  );

  const current = visible[index] ?? null;
  const urls = usePreviewWindow(visible, index);

  // Remember the position so reopening the shoot lands in the same place.
  useEffect(() => {
    if (current) api.setLastViewed(projectId, current.id);
  }, [api, projectId, current?.id]);

  /* ---------- stacks ---------- */

  const currentBurst = current ? (markers.get(current.id)?.burstId ?? null) : null;
  const behind = current ? (depth.get(current.id) ?? 0) : 0;

  /**
   * Open or close the burst under the cursor.
   *
   * Both re-find the current frame in the list that results, because changing
   * what is shown moves everything after it. Landing on a different photograph
   * than the one being looked at is the kind of thing that loses a photographer
   * their place mid-shoot.
   */
  const setStack = useCallback(
    (open: boolean) => {
      if (currentBurst === null) return false;
      if (open ? behind < 2 : !expanded.has(currentBurst)) return false;

      const next = new Set(expanded);
      if (open) next.add(currentBurst);
      else next.delete(currentBurst);

      const list = flatten(stacks, next);
      const stack = stacks.find((s) => s.burstId === currentBurst);
      // On collapse the current frame may be one of the ones now hidden, so
      // fall back to the frame the stack shows.
      const target = list.findIndex((item) => item.id === current?.id);

      setExpanded(next);
      setIndex(target >= 0 ? target : Math.max(0, list.findIndex((i) => i.id === stack?.face.id)));
      return true;
    },
    [currentBurst, behind, expanded, stacks, current?.id],
  );

  /* ---------- movement ---------- */

  const move = useCallback(
    (delta: number) => {
      setIndex((i) => Math.min(visible.length - 1, Math.max(0, i + delta)));
    },
    [visible.length],
  );

  /** Replace one item in place, dropping it if the current filter excludes it. */
  const applyLocal = useCallback(
    (mediaId: number, advance: boolean) => {
      const fresh = api.getMedia(mediaId);
      if (!fresh) return;

      setItems((list) => {
        const at = list.findIndex((item) => item.id === mediaId);
        if (at === -1) return list;

        if (!api.matchesFilter(fresh, filter)) {
          const next = [...list.slice(0, at), ...list.slice(at + 1)];
          setIndex((i) => Math.min(Math.max(0, next.length - 1), i));
          return next;
        }

        const next = [...list];
        next[at] = fresh;
        if (advance) setIndex((i) => Math.min(list.length - 1, i + 1));
        return next;
      });
      reloadBuckets();
    },
    [api, filter, reloadBuckets],
  );

  /* ---------- decisions ---------- */

  const assign = useCallback(
    (bucket: Bucket) => {
      if (!current) return;
      const change = api.toggleBucket(current.id, bucket.id);
      if (!change) return;

      past.current.push(change);
      future.current = [];
      bumpHistory((n) => n + 1);
      applyLocal(change.mediaId, project?.autoAdvance ?? true);
    },
    [api, current, project?.autoAdvance, applyLocal],
  );

  const clear = useCallback(() => {
    if (!current?.bucketId) return;
    const change = api.assignBucket(current.id, null);
    if (!change) return;

    past.current.push(change);
    future.current = [];
    bumpHistory((n) => n + 1);
    applyLocal(change.mediaId, false);
  }, [api, current, applyLocal]);

  const rotate = useCallback(
    (degrees: number) => {
      if (!current) return;
      api.rotate(current.id, degrees);
      applyLocal(current.id, false);
    },
    [api, current, applyLocal],
  );

  const undo = useCallback(() => {
    const change = past.current.pop();
    if (!change) return;

    api.revertBucket(change);
    future.current.push(change);
    bumpHistory((n) => n + 1);

    // Bring the photo back into view so the correction is visible.
    setFilterSafeFocus(change.mediaId);
    applyLocal(change.mediaId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, applyLocal]);

  const redo = useCallback(() => {
    const change = future.current.pop();
    if (!change) return;

    api.reapplyBucket(change);
    past.current.push(change);
    bumpHistory((n) => n + 1);
    applyLocal(change.mediaId, false);
  }, [api, applyLocal]);

  function setFilterSafeFocus(mediaId: number) {
    setItems((list) => {
      const at = list.findIndex((item) => item.id === mediaId);
      if (at >= 0) setIndex(at);
      return list;
    });
  }

  const cycleFilter = useCallback(() => {
    const at = FILTERS.findIndex((f) => JSON.stringify(f.value) === JSON.stringify(filter));
    setFilter(FILTERS[(at + 1) % FILTERS.length].value);
  }, [filter]);

  const toggleAutoAdvance = useCallback(() => {
    if (!project) return;
    api.setAutoAdvance(project.id, !project.autoAdvance);
    setProject(api.getProject(project.id));
  }, [api, project]);

  /* ---------- keyboard ---------- */

  useHotkeys(
    [
      { key: 'arrowright', run: () => move(1) },
      { key: 'arrowleft', run: () => move(-1) },
      { key: ' ', run: () => move(1) },
      { key: 'home', run: () => setIndex(0) },
      { key: 'end', run: () => setIndex(visible.length - 1) },
      { key: 'arrowright', meta: true, run: () => rotate(90) },
      { key: 'arrowleft', meta: true, run: () => rotate(-90) },
      { key: '0', run: clear },
      { key: 'backspace', run: clear },
      { key: 'z', shift: false, run: undo },
      { key: 'z', shift: true, run: redo },
      { key: 'a', run: toggleAutoAdvance },
      { key: 's', run: () => { setSortSharpest((v) => !v); setIndex(0); } },
      { key: 'enter', run: () => setStack(true) },
      { key: 'f', run: cycleFilter },
      { key: '?', run: () => setShowKeys((v) => !v) },
      { key: '/', shift: true, run: () => setShowKeys((v) => !v) },
      {
        key: 'escape',
        run: () => {
          if (showKeys) return setShowKeys(false);
          // Closing an open stack before leaving the shoot: Escape should undo
          // the last thing that was opened, not the whole screen.
          if (setStack(false)) return;
          navigate(`/p/${projectId}`);
        },
      },
      ...buckets.map((bucket) => ({
        key: bucket.hotkey,
        run: () => assign(bucket),
      })),
    ],
    !loading,
  );

  /* ---------- render ---------- */

  const reviewed = project ? project.reviewedCount : 0;
  const total = project ? project.itemCount : 0;

  const softCount = useMemo(
    () => items.reduce((n, item) => n + (markers.get(item.id)?.isSoft ? 1 : 0), 0),
    [items, markers],
  );

  const pickCount = useMemo(
    () => items.reduce((n, item) => n + (markers.get(item.id)?.isPick ? 1 : 0), 0),
    [items, markers],
  );

  const filterLabel = useMemo(
    () => FILTERS.find((f) => JSON.stringify(f.value) === JSON.stringify(filter))?.label ?? 'All',
    [filter],
  );

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-ink-600">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  return (
    <div id="main" className="flex h-full flex-col bg-ink-950">
      <header className="flex items-center gap-3 border-b border-ink-850 bg-ink-900 px-3 py-2">
        <button className="btn-ghost px-2" onClick={() => navigate(`/p/${projectId}`)}>
          <ChevronLeft size={16} />
        </button>

        <div className="min-w-0">
          <h1 className="truncate text-caption font-medium text-ink-50">{project?.name}</h1>
          <p className="tnum text-tiny text-ink-500">
            {visible.length > 0 ? `${index + 1} of ${visible.length}` : 'Nothing here'}
            {current && ` · ${current.filename}`}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="hidden items-center gap-2.5 sm:flex">
            <span className="tnum text-tiny text-ink-500">
              <span className="text-ink-200">{reviewed}</span> of {total} reviewed
            </span>
            <div className="w-20">
              <ProgressBar value={reviewed} total={total} />
            </div>
          </div>

          <div className="flex rounded border border-ink-700 p-0.5">
            {FILTERS.map((option) => (
              <button
                key={option.label}
                onClick={() => setFilter(option.value)}
                className={clsx(
                  'rounded-[3px] px-2 py-1 text-tiny transition',
                  option.label === filterLabel
                    ? 'bg-ink-750 text-ink-50'
                    : 'text-ink-400 hover:text-ink-100',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          <LensChip
            label="AI picks"
            count={pickCount}
            active={lens === 'picks'}
            title="Show only the frames Review Assist suggests"
            tone="pick"
            onClick={() => changeLens(lens === 'picks' ? 'all' : 'picks')}
          />

          <LensChip
            label="Blurry"
            count={softCount}
            active={lens === 'soft'}
            title="Show only frames that look out of focus. “Soft”, in photographer’s terms"
            tone="warn"
            onClick={() => changeLens(lens === 'soft' ? 'all' : 'soft')}
          />

          <button
            onClick={() => { setSortSharpest((v) => !v); setIndex(0); }}
            title="Sort sharpest first (S)"
            className={clsx(
              'flex items-center gap-1.5 rounded border px-2 py-1 text-tiny transition',
              sortSharpest
                ? 'border-ink-600 bg-ink-750 text-ink-50'
                : 'border-ink-700 text-ink-400 hover:text-ink-100',
            )}
          >
            <ArrowDownWideNarrow size={12} />
            Sharpest
          </button>

          <button className="btn-ghost px-2" onClick={() => setShowKeys(true)} title="Keyboard shortcuts">
            <Keyboard size={16} />
          </button>
        </div>
      </header>

      {visible.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-ink-500">
          <p className="text-caption">Nothing matches this filter.</p>
          <button className="btn-outline" onClick={() => setFilter('all')}>
            Show all photos
          </button>
        </div>
      ) : (
        <>
          <PreviewPane item={current} url={current ? (urls.get(current.id) ?? null) : null} buckets={buckets} />

          <BucketBar
            buckets={buckets}
            current={current}
            canUndo={past.current.length > 0}
            canRedo={future.current.length > 0}
            autoAdvance={project?.autoAdvance ?? true}
            onAssign={assign}
            onClear={clear}
            onRotate={rotate}
            onUndo={undo}
            onRedo={redo}
            onToggleAutoAdvance={toggleAutoAdvance}
          />

          <Filmstrip
            items={visible}
            index={index}
            buckets={buckets}
            markers={markers}
            depth={depth}
            onPick={setIndex}
          />
        </>
      )}

      {showKeys && <ShortcutSheet onClose={() => setShowKeys(false)} />}
    </div>
  );
}

/**
 * One of the two narrowing chips. Both behave identically and differ only in
 * colour and what they count, so they are one component rather than two.
 */
function LensChip({
  label,
  count,
  active,
  title,
  tone,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  title: string;
  tone: 'warn' | 'pick';
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={count === 0 && !active}
      className={clsx(
        'flex items-center gap-1.5 rounded border px-2 py-1 text-tiny transition disabled:opacity-40',
        active
          ? tone === 'warn'
            ? 'border-warn-line bg-warn-soft text-warn'
            : 'border-pick-line bg-pick-soft text-pick'
          : 'border-ink-700 text-ink-400 hover:text-ink-100',
      )}
    >
      <span className={clsx('h-1.5 w-1.5 rounded-full', tone === 'warn' ? 'bg-warn' : 'bg-pick')} />
      {label}
      <span className="tnum text-ink-500">{count}</span>
    </button>
  );
}
